import { z } from 'zod';
/**
 * Capability Registry
 *
 * Capabilities handle in-page permission checks that can't be expressed as
 * route access. This is a finite, code-defined set. The admin UI renders them
 * as toggles per group. New capabilities require a code change to register,
 * but which groups have them is fully dynamic.
 */
export const CAPABILITY_REGISTRY = {
    // Goals domain — same pages accessible to all, but only some users can edit/delete
    'goals:edit': 'Can edit goals, initiatives, and CSFs',
    'goals:delete': 'Can delete goals, initiatives, and CSFs',
    // Billing — program visibility override (admins/accounting see all programs)
    'billing:admin': 'Can see all programs (overrides program-scoped access)',
    // Billing — Manager Override editing across all workflows (Setup > Workflow Allocation)
    'billing:manage_overrides': 'Can edit Manager Overrides on the Setup Workflow Allocation tab',
    // Finance — sensitive data sections on otherwise-accessible pages
    'finance:sensitive': 'Can view sensitive financial data (Intel Income/Forecasting)',
    // Hiring — headcount planning vertical (route-level gate)
    'hiring:planning': 'Can view the headcount planning pages (Rate Card, Position Planning, Envelopes, Rollup)',
    // Hiring — position approval rights
    'hiring:approve': 'Can create position rows directly as approved (Position Planning); the envelope gate still runs server-side',
};
// ── Zod Schemas ─────────────────────────────────────────────────────────────
export const CapabilitySchema = z.enum(Object.keys(CAPABILITY_REGISTRY));
export const UserGroupSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    description: z.string(),
    isActive: z.boolean(),
    routes: z.array(z.string()),
    capabilities: z.array(CapabilitySchema),
    createdAt: z.date(),
    updatedAt: z.date(),
    createdBy: z.string(),
});
export const CreateUserGroupDataSchema = z.object({
    name: z.string().min(1),
    description: z.string().default(''),
    isActive: z.boolean().default(true),
    routes: z.array(z.string()).default([]),
    capabilities: z.array(CapabilitySchema).default([]),
    createdBy: z.string(),
});
export const UpdateUserGroupDataSchema = z.object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    isActive: z.boolean().optional(),
    routes: z.array(z.string()).optional(),
    capabilities: z.array(CapabilitySchema).optional(),
});
// ── Type Guards ─────────────────────────────────────────────────────────────
export const isValidUserGroup = (data) => {
    return UserGroupSchema.safeParse(data).success;
};
export const isValidCapability = (value) => {
    return CapabilitySchema.safeParse(value).success;
};
